import React, { useMemo } from 'react';
import { HiOutlineBanknotes, HiOutlineShoppingCart, HiOutlineTrophy, HiOutlineChartBar, HiOutlineReceiptPercent } from 'react-icons/hi2';
import ExpensesChart from './components/ExpensesChart/ExpensesChart';
import ProviderRanking from './components/ProviderRanking/ProviderRanking';
import IngredientBreakdown from './components/IngredientBreakdown/IngredientBreakdown';
import CategoryBreakdown from './components/CategoryBreakdown/CategoryBreakdown';
import TaxDistribution from './components/TaxDistribution/TaxDistribution';
import StatsFilterBar from './components/StatsFilterBar/StatsFilterBar';
import { IngredientPurchaseRecord, ExpenseCategoryType } from '../../types';
import './ComprasStats.scss';

interface FilterOption {
  value: string | number;
  label: string;
}

interface ComprasStatsProps {
  purchases: IngredientPurchaseRecord[];
  loading?: boolean;
  categories: ExpenseCategoryType[];
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  selectedProviderId: string;
  onProviderChange: (providerId: string) => void;
  providerOptions: FilterOption[];
}

const formatCurrency = (value: number) => 
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(Math.round(value));

const ComprasStats: React.FC<ComprasStatsProps> = ({ 
  purchases, 
  loading = false, 
  categories,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  selectedProviderId,
  onProviderChange,
  providerOptions,
}) => {
  
  // Calculate all statistics
  const stats = useMemo(() => {
    if (!purchases || purchases.length === 0) {
      return {
        totalSpent: 0,
        highestCostIngredient: null as { name: string; cost: number } | null,
        mostPurchasedIngredient: null as { name: string; count: number } | null,
        averagePurchaseValue: 0,
        purchaseCount: 0,
        totalWithTax: 0,
        totalTaxAmount: 0,
      };
    }

    const totalSpent = purchases.reduce((sum, p) => sum + p.total_neto, 0);
    const purchaseCount = purchases.length;
    const averagePurchaseValue = totalSpent / purchaseCount;
    const totalWithTax = purchases.reduce((sum, p) => sum + (p.total_with_tax || p.total_neto), 0);
    const totalTaxAmount = purchases.reduce((sum, p) => sum + (p.tax_amount || 0), 0);

    // Group by ingredient to find highest cost and most purchased
    const ingredientMap = new Map<string, { totalCost: number; count: number }>();
    
    purchases.forEach(p => {
      const name = p.ingredient_name || 'Sin nombre';
      const existing = ingredientMap.get(name) || { totalCost: 0, count: 0 };
      ingredientMap.set(name, {
        totalCost: existing.totalCost + p.total_neto,
        count: existing.count + 1,
      });
    });

    let highestCostIngredient: { name: string; cost: number } | null = null;
    let mostPurchasedIngredient: { name: string; count: number } | null = null;

    ingredientMap.forEach((data, name) => {
      if (!highestCostIngredient || data.totalCost > highestCostIngredient.cost) {
        highestCostIngredient = { name, cost: data.totalCost };
      }
      if (!mostPurchasedIngredient || data.count > mostPurchasedIngredient.count) {
        mostPurchasedIngredient = { name, count: data.count };
      }
    });

    return {
      totalSpent,
      highestCostIngredient,
      mostPurchasedIngredient,
      averagePurchaseValue,
      purchaseCount,
      totalWithTax,
      totalTaxAmount,
    };
  }, [purchases]);

  if (loading) {
    return (
      <div className="compras-stats-container">
        <div className="loading-message">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando estadísticas...</span>
          </div>
          <p>Cargando estadísticas de compras...</p>
        </div>
      </div>
    );
  }

  if (!purchases || purchases.length === 0) {
    return (
      <div className="compras-stats-container">
        <div className="no-data-message">
          <HiOutlineShoppingCart size={48} />
          <h3>No hay datos disponibles</h3>
          <p>No se encontraron compras en el período seleccionado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="compras-stats-container">
      {/* Filter Bar */}
      <StatsFilterBar
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        selectedProviderId={selectedProviderId}
        onProviderChange={onProviderChange}
        providerOptions={providerOptions}
      />

      {/* KPI Cards */}
      <div className="stats-kpi-cards">
        <div className="kpi-card total-spent">
          <div className="kpi-icon">
            <HiOutlineBanknotes />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Total Gastado</div>
            <div className="kpi-value">{formatCurrency(stats.totalSpent)}</div>
            <div className="kpi-subtitle">{stats.purchaseCount} gastos registrados</div>
          </div>
        </div>

        <div className="kpi-card highest-cost">
          <div className="kpi-icon">
            <HiOutlineTrophy />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Mayor Costo</div>
            <div className="kpi-value">
              {stats.highestCostIngredient 
                ? formatCurrency(stats.highestCostIngredient.cost)
                : '-'
              }
            </div>
            <div className="kpi-subtitle">
              {stats.highestCostIngredient?.name || '-'}
            </div>
          </div>
        </div>

        <div className="kpi-card most-purchased">
          <div className="kpi-icon">
            <HiOutlineShoppingCart />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Más Comprado</div>
            <div className="kpi-value">
              {stats.mostPurchasedIngredient?.count || 0}x
            </div>
            <div className="kpi-subtitle">
              {stats.mostPurchasedIngredient?.name || '-'}
            </div>
          </div>
        </div>

        <div className="kpi-card average-value">
          <div className="kpi-icon">
            <HiOutlineChartBar />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">Valor Promedio</div>
            <div className="kpi-value">{formatCurrency(stats.averagePurchaseValue)}</div>
            <div className="kpi-subtitle">por compra</div>
          </div>
        </div>

        <div className="kpi-card iva-total">
          <div className="kpi-icon">
            <HiOutlineReceiptPercent />
          </div>
          <div className="kpi-content">
            <div className="kpi-label">IVA Total</div>
            <div className="kpi-value">{formatCurrency(stats.totalTaxAmount)}</div>
            <div className="kpi-subtitle">Total c/IVA: {formatCurrency(stats.totalWithTax)}</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="stats-charts-section">
        <ExpensesChart purchases={purchases} />
      </div>

      {/* Rankings Section */}
      <div className="stats-rankings-section">
        <ProviderRanking purchases={purchases} />
        <IngredientBreakdown purchases={purchases} />
      </div>

      {/* Category & Tax Section */}
      <div className="stats-rankings-section">
        <CategoryBreakdown purchases={purchases} />
        <TaxDistribution purchases={purchases} />
      </div>
    </div>
  );
};

export default ComprasStats;
